// components/marketing/ListFactoryToggle.tsx
'use client';

import { SegmentSwitch } from '@/components/ui/SegmentSwitch';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { isTableFocusSubject, selectActiveSubjectFromInventoryState } from '#lib/leasing/session-focus';
import { LEGACY_COMPAT_USER_ACTIVITIES, normalizeUserActivities } from '#lib/user-activities';
import type { KernelState } from '#types/types';

function canSwitch(state: KernelState) {
  const subject = selectActiveSubjectFromInventoryState(state);
  return Boolean(subject && !state.portalStandaloneView && (state.studioSection === 'list' || state.studioSection === 'factory') &&
    isTableFocusSubject(subject) && subject.tableView === 'projects' &&
    Boolean(state.userContext.permissions?.canAccessFactory) &&
    normalizeUserActivities(state.userPreferences.activities, { fallback: LEGACY_COMPAT_USER_ACTIVITIES }).includes('DESIGN'));
}

export function ListFactoryToggle() {
  const { kernel } = useKernel();
  const available = useKernelStore(canSwitch);
  const section = useKernelStore(state => state.studioSection);
  const french = useKernelStore(state => state.language === 'fr');
  if (!available) return null;

  const options = [
    { key: 'list', label: french ? 'Tableau de bord' : 'Dashboard' },
    { key: 'factory', label: 'Factory', tour: { attribute: 'data-factory-tour', value: 'studio-factory' } },
  ] as const;

  return <SegmentSwitch groupLabel={french ? 'Vue List' : 'List view'}
    options={options} value={section === 'factory' ? 'factory' : 'list'}
    onChange={destination => {
      const k = kernel.current;
      if (!k || !canSwitch({ ...k.getState(), inventory: k.getInventory() }) || k.getStudioSection() === destination) return;
      if ([...document.querySelectorAll<HTMLElement>('dialog[open], [role="dialog"][aria-modal="true"]')].some(modal => modal.getClientRects().length > 0)) return;
      k.setStudioSection(destination);
      k.logUserAction(destination === 'factory' ? 'Factory opened from List dashboard' : 'List dashboard opened from Factory');
    }} />;
}
